import React, { Component } from 'react';
import FundInvestmentInvestor from '../../../models/admin/FundInvestmentData';
import InvestorManagementMainContent from './InvestorManagementMainContent';
import InvestorManagementOverlays from './InvestorManagementOverlays';
import InvestorBulkPromoteModal from './InvestorBulkPromoteModal';
import InvestorInvestmentPromptModal from './InvestorInvestmentPromptModal';
import { buildInvestorContactCards } from './buildInvestorContactCards';
import {
  runAddAllCandidates,
  runDemoteInvestor,
  runDeleteInvestor,
  runPromoteInvestor,
  runSubmitInvestmentPrompt,
} from './investorManagementHandlers';
import {
  buildBulkPromoteInvestors,
  getNextStage,
  requiresInvestmentAmount,
  updateBulkPromoteInvestorAmountField,
  updateBulkPromoteInvestorField,
} from './investorPromotionUtils';
import type {
  BulkPromoteStage,
  InvestorManagementProps,
  InvestorManagementState,
} from './investorManagementTypes';
import {
  confirmPromotionPrerequisiteRetry,
  runWithPromotionPrerequisites,
} from './runWithPromotionPrerequisites';
import { submitBulkPromotionRequest } from './submitBulkPromotion';

export type { InvestorManagementProps, InvestorManagementState };

export class InvestorManagementImpl extends Component<
  InvestorManagementProps,
  InvestorManagementState
> {
  constructor(props: InvestorManagementProps) {
    super(props);
    this.state = {
      bulkPromoteModalOpen: false,
      investmentPromptModalOpen: false,
      addAllInProgress: false,
      bulkPromoteInProgress: false,
      investmentPromptInProgress: false,
    };
  }

  private getPrerequisiteUiState = () => ({
    investmentPromptModalOpen: this.state.investmentPromptModalOpen,
    investorActionInProgress: this.state.investorActionInProgress,
    actionTypeInProgress: this.state.actionTypeInProgress,
    investmentPromptInProgress: this.state.investmentPromptInProgress,
  });

  private setPrerequisiteUiState = (
    update:
      | Partial<InvestorManagementState>
      | ((prev: InvestorManagementState) => Partial<InvestorManagementState>),
  ) => {
    if (typeof update === 'function') {
      this.setState((prev) => ({ ...prev, ...update(prev) }));
    } else {
      this.setState((prev) => ({ ...prev, ...update }));
    }
  };

  private handleInvestorOperationWithPrerequisites = (
    run: (opts: { overrideFundStatusRestriction?: boolean }) => Promise<void>,
    onSuccess: () => void,
    relaxUiWhileAwaitingAck = false,
  ) => {
    runWithPromotionPrerequisites(
      run,
      onSuccess,
      this.getPrerequisiteUiState,
      this.setPrerequisiteUiState,
      relaxUiWhileAwaitingAck,
    );
  };

  closePromotionPrerequisiteModal = () => {
    this.setState({
      promotionPrerequisiteModalOpen: false,
      promotionPrerequisiteIssues: [],
      promotionPrerequisiteRetry: undefined,
      promotionPrerequisiteOnSuccess: undefined,
      promotionPrerequisiteConfirmInProgress: false,
      investorActionInProgress: undefined,
      actionTypeInProgress: undefined,
      investmentPromptInProgress: false,
    });
  };

  confirmPromotionPrerequisiteModal = () => {
    confirmPromotionPrerequisiteRetry(
      this.state.promotionPrerequisiteRetry,
      this.state.promotionPrerequisiteOnSuccess,
      () => ({ investmentPromptModalOpen: this.state.investmentPromptModalOpen }),
      this.setPrerequisiteUiState,
    );
  };

  requiresInvestmentAmount = requiresInvestmentAmount;
  getNextStage = getNextStage;

  promoteInvestor(investor: FundInvestmentInvestor) {
    this.setState({ investorActionInProgress: investor.id, actionTypeInProgress: 'promote' });
    runPromoteInvestor(
      investor,
      this.handleInvestorOperationWithPrerequisites,
      () => this.props.onDataRefresh(),
      (patch) =>
        this.setState({
          investmentPromptModalOpen: true,
          investmentPromptError: undefined,
          ...patch,
        }),
      () => this.setState({ investorActionInProgress: undefined, actionTypeInProgress: undefined }),
    );
  }

  demoteInvestor = (investor: FundInvestmentInvestor) => {
    this.setState({ investorActionInProgress: investor.id, actionTypeInProgress: 'demote' });
    runDemoteInvestor(
      investor,
      this.handleInvestorOperationWithPrerequisites,
      () => this.props.onDataRefresh(),
      () => this.setState({ investorActionInProgress: undefined, actionTypeInProgress: undefined }),
    );
  };

  deleteInvestor(investor: FundInvestmentInvestor) {
    this.setState({ investorActionInProgress: investor.id, actionTypeInProgress: 'delete' });
    runDeleteInvestor(
      investor,
      () => this.props.onDataRefresh(),
      () => this.setState({ investorActionInProgress: undefined, actionTypeInProgress: undefined }),
    );
  }

  promoteStageInvestors = (stage: BulkPromoteStage, investorIds?: string[]) => {
    if (!this.props.data) return;
    const eligibleInvestors = investorIds
      ? this.props.data.investors.filter((investor) => investorIds.includes(investor.id))
      : this.props.data.investors;
    this.setState({
      bulkPromoteModalOpen: true,
      bulkPromoteStage: stage === 'LEAD_WITH_EOI' ? 'LEAD' : stage,
      bulkPromoteInvestors: buildBulkPromoteInvestors(eligibleInvestors, stage),
    });
  };

  closeBulkPromoteModal = () => {
    this.setState({
      bulkPromoteModalOpen: false,
      bulkPromoteStage: undefined,
      bulkPromoteInvestors: undefined,
    });
  };

  updateBulkPromoteInvestor = (id: string, field: string, value: unknown) => {
    this.setState({
      bulkPromoteInvestors: updateBulkPromoteInvestorField(
        this.state.bulkPromoteInvestors,
        id,
        field,
        value,
      ),
    });
  };

  updateBulkPromoteInvestorAmount = (
    id: string,
    field: 'preferredInvestment' | 'committedInvestment',
    value: string,
  ) => {
    const numericValue = value === '' ? 0 : parseFloat(value);
    if (isNaN(numericValue)) return;
    this.setState({
      bulkPromoteInvestors: updateBulkPromoteInvestorAmountField(
        this.state.bulkPromoteInvestors,
        id,
        field,
        value,
      ),
    });
  };

  removeBulkPromoteInvestor = (id: string) => {
    this.setState({
      bulkPromoteInvestors: this.state.bulkPromoteInvestors?.filter((item) => item.id !== id),
    });
  };

  submitBulkPromotion = async () => {
    const { bulkPromoteInvestors, bulkPromoteStage } = this.state;
    if (!bulkPromoteInvestors || !bulkPromoteStage) return;

    this.setState({ bulkPromoteInProgress: true });
    try {
      await submitBulkPromotionRequest(bulkPromoteInvestors, bulkPromoteStage);
      this.setState({ bulkPromoteInProgress: false });
      this.closeBulkPromoteModal();
      this.props.onDataRefresh();
    } catch (error) {
      this.setState({ bulkPromoteInProgress: false });
      console.error('Error during bulk promotion:', error);
      alert('An error occurred during bulk promotion. Please try again.');
    }
  };

  closeInvestmentPromptModal = () => {
    this.setState({
      investmentPromptModalOpen: false,
      investmentPromptStage: undefined,
      investmentPromptInvestor: undefined,
      investmentPromptAmount: '',
      investmentPromptError: undefined,
      investmentPromptInProgress: false,
    });
  };

  handleInvestmentPromptAmountChange = (value: string) => {
    this.setState({ investmentPromptAmount: value, investmentPromptError: undefined });
  };

  submitInvestmentPrompt = () => {
    const { investmentPromptInvestor, investmentPromptStage, investmentPromptAmount } = this.state;
    if (!investmentPromptInvestor || !investmentPromptStage) return;

    const amount = parseFloat(investmentPromptAmount || '0');
    if (amount <= 0) {
      this.setState({ investmentPromptError: 'Please enter a valid investment amount' });
      return;
    }

    this.setState({
      investmentPromptInProgress: true,
      investorActionInProgress: investmentPromptInvestor.id,
      actionTypeInProgress: 'promote',
    });

    runSubmitInvestmentPrompt(
      investmentPromptInvestor,
      investmentPromptStage,
      investmentPromptAmount,
      this.handleInvestorOperationWithPrerequisites,
      () => {
        this.setState({
          investmentPromptInProgress: false,
          investorActionInProgress: undefined,
          actionTypeInProgress: undefined,
        });
        this.closeInvestmentPromptModal();
        this.props.onDataRefresh();
      },
      () => undefined,
      (message) =>
        this.setState({
          investmentPromptError: message,
          investmentPromptInProgress: false,
          investorActionInProgress: undefined,
          actionTypeInProgress: undefined,
        }),
    );
  };

  getContactsForStage(
    stage: FundInvestmentInvestor['status'],
    options: { withInvestment?: boolean; withoutInvestment?: boolean } = {},
  ) {
    return buildInvestorContactCards(
      this.props.data?.investors,
      stage,
      this.props.investorFilter,
      options,
      {
        onPromote: (investor) => this.promoteInvestor(investor),
        onDemote: (investor) => this.demoteInvestor(investor),
        onDelete: (investor) => this.deleteInvestor(investor),
      },
    );
  }

  handleAddAllCandidates = () => {
    runAddAllCandidates(
      this.props.fundOpportunityId,
      () => this.props.onDataRefresh(),
      (inProgress) => this.setState({ addAllInProgress: inProgress }),
    );
  };

  renderInvestmentPromptModal() {
    const {
      investmentPromptModalOpen,
      investmentPromptStage,
      investmentPromptInvestor,
      investmentPromptAmount,
      investmentPromptError,
    } = this.state;

    return (
      <InvestorInvestmentPromptModal
        open={investmentPromptModalOpen}
        stage={investmentPromptStage}
        investor={investmentPromptInvestor}
        amount={investmentPromptAmount}
        error={investmentPromptError}
        onClose={this.closeInvestmentPromptModal}
        onAmountChange={this.handleInvestmentPromptAmountChange}
        onSubmit={this.submitInvestmentPrompt}
      />
    );
  }

  renderBulkPromoteModal() {
    const { bulkPromoteModalOpen, bulkPromoteStage, bulkPromoteInvestors } = this.state;
    if (!bulkPromoteStage) return null;

    return (
      <InvestorBulkPromoteModal
        open={bulkPromoteModalOpen}
        stage={bulkPromoteStage}
        investors={bulkPromoteInvestors}
        onClose={this.closeBulkPromoteModal}
        onSubmit={this.submitBulkPromotion}
        onUpdateAmount={this.updateBulkPromoteInvestorAmount}
        onRemoveInvestor={this.removeBulkPromoteInvestor}
      />
    );
  }

  renderInvestors() {
    return (
      <InvestorManagementMainContent
        loaded={this.props.loaded}
        data={this.props.data}
        highlightedInvestorId={this.props.highlightedInvestorId}
        investorFilter={this.props.investorFilter}
        onInvestorFilterChange={this.props.onInvestorFilterChange}
        hasPermission={this.props.hasPermission}
        showAddInvestor={this.props.showAddInvestor}
        onShowAddInvestorChange={this.props.onShowAddInvestorChange}
        onDataRefresh={this.props.onDataRefresh}
        addAllInProgress={this.state.addAllInProgress}
        bulkPromoteInProgress={this.state.bulkPromoteInProgress}
        bulkPromoteStage={this.state.bulkPromoteStage}
        investorActionInProgress={this.state.investorActionInProgress}
        actionTypeInProgress={this.state.actionTypeInProgress}
        onAddAllCandidates={this.handleAddAllCandidates}
        onBulkPromote={(stage, investorIds) => this.promoteStageInvestors(stage, investorIds)}
        getContactsForStage={this.getContactsForStage.bind(this)}
      />
    );
  }

  render() {
    return (
      <>
        {this.renderInvestors()}
        <InvestorManagementOverlays
          investmentPromptModalOpen={this.state.investmentPromptModalOpen}
          investmentPromptStage={this.state.investmentPromptStage}
          investmentPromptInvestor={this.state.investmentPromptInvestor}
          investmentPromptAmount={this.state.investmentPromptAmount}
          investmentPromptError={this.state.investmentPromptError}
          bulkPromoteModalOpen={this.state.bulkPromoteModalOpen}
          bulkPromoteStage={this.state.bulkPromoteStage}
          bulkPromoteInvestors={this.state.bulkPromoteInvestors}
          promotionPrerequisiteModalOpen={this.state.promotionPrerequisiteModalOpen}
          promotionPrerequisiteIssues={this.state.promotionPrerequisiteIssues}
          promotionPrerequisiteConfirmInProgress={this.state.promotionPrerequisiteConfirmInProgress}
          onCloseInvestmentPrompt={this.closeInvestmentPromptModal}
          onInvestmentPromptAmountChange={this.handleInvestmentPromptAmountChange}
          onSubmitInvestmentPrompt={this.submitInvestmentPrompt}
          onCloseBulkPromote={this.closeBulkPromoteModal}
          onSubmitBulkPromotion={this.submitBulkPromotion}
          onUpdateBulkPromoteAmount={this.updateBulkPromoteInvestorAmount}
          onRemoveBulkPromoteInvestor={this.removeBulkPromoteInvestor}
          onClosePromotionPrerequisite={this.closePromotionPrerequisiteModal}
          onConfirmPromotionPrerequisite={this.confirmPromotionPrerequisiteModal}
        />
      </>
    );
  }
}

export default InvestorManagementImpl;
